import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  devLogin,
  fetchSession,
  logout as logoutRequest,
  passwordLogin,
} from "./auth-client";
import { type DevLoginInput } from "./auth-client";
import { type SessionUser } from "./types";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  /** STUB dev login; kept only for local e2e. */
  signInWithDevLogin: (input?: DevLoginInput) => Promise<void>;
  /** Interim email+password sign-in (DMS auth plan). */
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);

  const apply = useCallback((next: SessionUser | null) => {
    setUser(next);
    setStatus(next ? "authenticated" : "unauthenticated");
  }, []);

  useEffect(() => {
    let active = true;
    fetchSession()
      .then((session) => {
        if (active) apply(session);
      })
      .catch(() => {
        if (active) apply(null);
      });
    return () => {
      active = false;
    };
  }, [apply]);

  // Re-validate the session when the tab regains focus. A user can leave the app
  // open past the session's lifetime (8h); without this the SPA keeps showing an
  // authenticated shell over a dead session, and the next action fails with a
  // confusing error instead of a clean re-login. Silent — it never flips status
  // back to "loading", so a still-valid session causes no flicker, while an
  // expired one resolves to null and the auth guards redirect to /login.
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      fetchSession()
        .then((session) => apply(session))
        .catch(() => apply(null));
    };
    document.addEventListener("visibilitychange", revalidate);
    return () => document.removeEventListener("visibilitychange", revalidate);
  }, [apply]);

  const signInWithDevLogin = useCallback(
    async (input?: DevLoginInput) => {
      apply(await devLogin(input));
    },
    [apply],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      apply(await passwordLogin(email, password));
    },
    [apply],
  );

  const signOut = useCallback(async () => {
    await logoutRequest();
    apply(null);
  }, [apply]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signInWithDevLogin, signInWithPassword, signOut }),
    [status, user, signInWithDevLogin, signInWithPassword, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within an AuthProvider");
  return value;
};
